import { formatISO } from 'date-fns';
import { createSelector } from 'reselect';

import { DISPLAY_UNCOMPLETED_PAYMENT, FROM, PAYMENT_GATEWAYS, PAYMENT_PROVIDERS, TO, STATUS } from 'constants/FilterConstants';
import { PAYMENT_PROVIDER, PaymentStatus } from 'constants/PaymentConstants';
import { getLocale } from 'modules/i18n/selectors/data';
import { getSelectedDealerId } from 'selectors/dealerSelectors';

import { filterRequestFormatter } from '../../interventions/utils';

/** @returns {import('../reducer').PaymentReducer} */
const getState = state => state.payments;

const getRoot = root => root;

const getProps = (_, props) => props;

const getUIState = createSelector(getState, state => state.ui);

const getDataState = createSelector(getState, state => state.data);

const getPaymentEntries = createSelector(getDataState, state => state.entities);

const getPaymentsById = createSelector(
  getPaymentEntries,
  getProps,
  (entries, paymentIds = []) => paymentIds.reduce((acc, paymentId) => {
    if (entries[paymentId]) {
      acc.push(entries[paymentId]);
    }
    return acc;
  }, [])
);

const getUIDashboard = createSelector(getUIState, state => state.dashboard);

const getUIDashboardData = createSelector(getUIDashboard, state => state.data);

const getDashboardIds = createSelector(getUIDashboardData, state => state.ids);

export const getDashboardPage = createSelector(getUIDashboardData, state => state.page);

export const isDashboardLoading = createSelector(getUIDashboardData, state => state.loading);

export const getDashboardEntries = createSelector(getRoot, getDashboardIds, getPaymentsById);

export const getFilters = createSelector(
  getUIDashboard,
  state => state.filters
);

export const getPersistedFilters = createSelector(
  getUIDashboard,
  state => {
    const { filters } = state;
    return {
      ...filters,
      [FROM]: undefined,
      [TO]: undefined,
    };
  }
);

const providerRequestFormatter = (providers = []) => {
  const paymentProviders = Object.values(PAYMENT_PROVIDER);
  return providers.reduce((acc, provider) => {
    const FIELD = paymentProviders.includes(provider) ? PAYMENT_PROVIDERS : PAYMENT_GATEWAYS;
    return {
      ...acc,
      [FIELD]: [...acc[FIELD], provider],
    };
  }, { [PAYMENT_PROVIDERS]: [], [PAYMENT_GATEWAYS]: [] });
};

export const getBaseRequestParams = createSelector(
  getDashboardPage,
  getSelectedDealerId,
  getFilters,
  ({ size, number }, dealerId, { [DISPLAY_UNCOMPLETED_PAYMENT]: displayUncompleted, [PAYMENT_PROVIDERS]: providers, ...filters }) => ({
    size,
    dealerId,
    page: number,
    ...(!displayUncompleted ? { [STATUS]: [PaymentStatus.SUCCESS] } : {}),
    ...filterRequestFormatter({
      ...filters,
      ...providerRequestFormatter(providers),
    }),
  })
);

const getTotalAmountState = createSelector(getUIDashboard, state => state.amount);

export const getTotalAmount = createSelector(getTotalAmountState, amount => amount.total);

export const isExportModalOpen = createSelector(getUIDashboard, state => state.visible);

export const isExporting = createSelector(getUIDashboard, state => state.loading);

export const getPaymentExportParams = createSelector(
  getLocale,
  getFilters,
  getSelectedDealerId,
  (language, { [DISPLAY_UNCOMPLETED_PAYMENT]: displayUncompleted, [PAYMENT_PROVIDERS]: providers, ...filters }, dealerId) => ({
    language,
    filters: {
      ...(!displayUncompleted ? { [STATUS]: [PaymentStatus.SUCCESS] } : {}),
      ...filterRequestFormatter({
        ...filters,
        ...(filters[FROM] ? { [FROM]: formatISO(filters[FROM]) } : {}),
        ...(filters[TO] ? { [TO]: formatISO(filters[TO]) } : {}),
        dealerId
      }),
      ...providerRequestFormatter(providers),
    },
  })
);
